import { Outlet, useLocation, useNavigate } from "react-router-dom";
import SideMenu from "../Components/SideMenu";
import Modal from "../Components/Modal";
import { AuthContext, AuthContextType, LangContext, LangContextType, LoadingContext, LoadingContextType, SidebarContext, ToastContext, ToastContextType, useSidebar } from "../utilities/customHooks";
import { useContext, useState } from "react";
import LoadingIndicator from "../Components/LoadingIndicator";
import Breadcrumb from "../Components/Breadcrumb";
import ActionIndicator from "../Components/ActionIndicator";
import ImpersonationBanner from "../Components/ImpersonationBanner";
import UserSwitcher from "../Components/UserSwitcher";
import ViewLegacyToggle from "../Components/ViewLegacyToggle";
import { StickyHeaderProvider, useStickyHeader } from "../Components/StickyHeaderContext";
import textData from "../textData.json";
import { getBreadcrumbRoutes } from "../utilities/breadcrumbRoutes";
import { getUserLanguage } from "../utilities/helperFunctions";
import { setCommonSettings } from "../services/userService";
import * as T from '../utilities/frontendTypes';


function Dashboard() {

  const { langState, setLang } = useContext(LangContext) as LangContextType
  const { isAuthenticated, userRole, userRegion, userEmail, isVerified, requiresPasswordReset, settings, logout, setSettings, isImpersonating } = useContext(AuthContext) as AuthContextType; // [NOTE] This casting is intentional
  const {showLoading, hideLoading} = useContext(LoadingContext) as LoadingContextType;
  const { showToast } = useContext(ToastContext) as ToastContextType;
  const location = useLocation();
  const { pathname } = location;
  const sidebar = useSidebar();
  const navigate = useNavigate();

  // Prompt shown after a founder switches the dashboard language, offering to
  // also switch their email communications to match (managed on the settings page).
  // Holds the target language ("eng" | "fr") the prompt is offering, or null when hidden.
  const [emailLangPromptTarget, setEmailLangPromptTarget] = useState<"eng" | "fr" | null>(null);


    // Make sure Dashboard and all child components only renders after auth hook completes
    if (isAuthenticated === false || userRole === null || userRegion === null) {
      return <LoadingIndicator delay />
    }


  const handleLangToggle = async () => {
    const lang = langState === "eng" ? "fr" : "eng";

    const base: T.Settings = settings ?? {
      themeMode: "light",
      lang: getUserLanguage() === "Fr-fr" ? "fr" : "eng",
    };
    setSettings({ ...base, lang });

    // View As is read-only on the server (mutations 403) — switch the UI locally
    // for the impersonator and skip persisting to the target user's record.
    if (isImpersonating) return;

    showLoading();
    try {
      // R16: persist ONLY the changed field. The server merges per-field, so sending
      // the whole (possibly stale) `settings` snapshot could clobber a newer emailLang
      // / themeMode written elsewhere.
      await setCommonSettings({ lang });
    } catch {
      // Roll the context back so the UI reflects the true persisted state.
      setSettings(base);
      showToast({ toastType: "failure", message: textData.Pages.FounderSettings.DisplayLanguageSaveFail[langState as "eng" | "fr"] });
      return;
    } finally {
      hideLoading();
    }

    // When switching the dashboard language, offer to also switch email
    // communications to match — unless they've already opted into that language.
    if (base.emailLang !== lang) {
      setEmailLangPromptTarget(lang);
    }
  }

  const breadcrumbRoutes = getBreadcrumbRoutes(langState as "eng" | "fr");

  const emailLangPrompt = textData.Pages.FounderSettings.EmailLangPrompt;
  const emailLangPromptCopy = emailLangPromptTarget === "fr" ? emailLangPrompt.ToFrench : emailLangPrompt.ToEnglish;

  return (
    <SidebarContext.Provider value={sidebar}>
      <Modal
        isOpen={emailLangPromptTarget !== null}
        closeModal={() => setEmailLangPromptTarget(null)}
        contentLabel="Email language preference"
        modalHTML={
          <div className="flex flex-col gap-5 text-center text-text mb-6">
            {/* English first, then French below — so an accidental toggle is understandable either way. */}
            <div>
              <p className="text-2xl font-semibold mb-2">{emailLangPromptCopy.Title.eng}</p>
              <p>{emailLangPromptCopy.Body.eng}</p>
            </div>
            <div className="text-muted-foreground">
              <p className="text-2xl font-semibold mb-2">{emailLangPromptCopy.Title.fr}</p>
              <p>{emailLangPromptCopy.Body.fr}</p>
            </div>
          </div>
        }
        primaryButton={{
          buttonText: `${emailLangPrompt.Confirm.eng} / ${emailLangPrompt.Confirm.fr}`,
          buttonCallback: () => {
            setEmailLangPromptTarget(null);
            navigate("/dashboard/preferences");
          },
        }}
        secondaryButton={{
          buttonText: `${emailLangPrompt.Dismiss.eng} / ${emailLangPrompt.Dismiss.fr}`,
          buttonCallback: () => setEmailLangPromptTarget(null),
        }}
      />
      <div className="flex w-full h-full ">
        <SideMenu />
        <div className="flex flex-col grow overflow-y-auto" data-scroll-container>
          {!sidebar.shouldHideHeader && (
            <div className="sticky top-0 z-50 flex w-full h-14 px-6 justify-end items-center border-b border-border bg-background shrink-0">
              <div className='flex items-center gap-4'>
                <UserSwitcher />
                <ViewLegacyToggle />
                {
                userRole === "startupFounder" &&
                  <button className='flex bodyLarger buttonSizer ' onClick={() => handleLangToggle()}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none" className='mr-2 relative bottom-0.5'><circle cx="8" cy="8.5" r="7.5" stroke="#000000"/><path d="M10.5 8.5C10.5 10.6651 10.1701 12.6029 9.65316 13.9813C9.39401 14.6723 9.0975 15.1969 8.79361 15.5396C8.4892 15.883 8.21987 16 8 16C7.78013 16 7.5108 15.883 7.20639 15.5396C6.9025 15.1969 6.60599 14.6723 6.34684 13.9813C5.82994 12.6029 5.5 10.6651 5.5 8.5C5.5 6.33494 5.82994 4.39713 6.34684 3.01871C6.60599 2.32765 6.9025 1.80314 7.20639 1.46038C7.5108 1.11704 7.78013 1 8 1C8.21987 1 8.4892 1.11704 8.79361 1.46038C9.0975 1.80314 9.39401 2.32765 9.65316 3.01871C10.1701 4.39713 10.5 6.33494 10.5 8.5Z" stroke="#000000"/><path d="M0.5 8.5H15.5" stroke="#000000" strokeLinecap="round"/></svg>
                      <span className='relative '>{langState === "eng" ? "ENG" : "FR"}</span>
                  </button>
                }
                <button
                  className='flex bodyLarger buttonSizer'
                  onClick={logout}
                >
                  <svg className='mr-2 relative bottom-0.5' xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none"><path d="M2.60117 12.6826L2.62197 12.7105L2.62194 12.7105C3.31089 13.6256 4.21794 14.3783 5.26877 14.8979C6.29021 15.403 7.45043 15.6879 8.68188 15.6879C10.7469 15.6879 12.616 14.8831 13.9688 13.5821C15.3215 12.2811 16.1575 10.4843 16.1575 8.50003C16.1575 6.51571 15.3214 4.71894 13.9688 3.41797C12.616 2.11692 10.7469 1.31213 8.68188 1.31213C7.45417 1.31213 6.29756 1.59507 5.27926 2.09609C4.22058 2.61748 3.30739 3.37501 2.61589 4.29603C2.47204 4.48838 2.19528 4.53117 1.99654 4.39366C1.7966 4.25534 1.75032 3.98628 1.89506 3.79278L1.89513 3.7927C2.66968 2.76184 3.69136 1.91429 4.87674 1.33069C6.02011 0.767551 7.31393 0.45 8.68198 0.45C10.9922 0.45 13.0844 1.3506 14.5986 2.80692L14.5986 2.80692C16.1128 4.26383 17.05 6.27677 17.05 8.5C17.05 10.7236 16.1128 12.7367 14.5986 14.1931L14.5986 14.1931C13.0838 15.6494 10.9917 16.55 8.68198 16.55C7.30756 16.55 6.00899 16.2298 4.86243 15.6631C3.6733 15.0754 2.65003 14.2223 1.8771 13.1858L1.87704 13.1857C1.73295 12.9917 1.77975 12.7217 1.98023 12.5834C2.17965 12.4458 2.45741 12.4898 2.60117 12.6826ZM1.08524 8.18019L1.11158 8.15552L3.75033 5.61765C3.9241 5.44997 4.20534 5.45004 4.37955 5.61756C4.55497 5.78575 4.55487 6.05954 4.37958 6.22813L4.34492 6.19209L4.37957 6.22813L2.47601 8.05837H11.5025C11.7472 8.05837 11.9485 8.24931 11.9485 8.48921C11.9485 8.72918 11.7471 8.92005 11.5025 8.92005H2.47599L4.37952 10.7508C4.55495 10.919 4.55488 11.1927 4.37964 11.3613C4.20586 11.529 3.9246 11.5289 3.75039 11.3614L1.08524 8.79814C1.08516 8.79807 1.08508 8.79799 1.085 8.79791C1.06218 8.7765 1.04208 8.75257 1.02468 8.72795L1.0243 8.72822L1.02255 8.72485L1.01816 8.71829L1.0134 8.71117L1.01314 8.71134L1.01082 8.70687C0.972479 8.64331 0.950001 8.56882 0.950001 8.48917C0.950001 8.40969 0.972374 8.33537 1.01055 8.2719L1.01031 8.27177L1.0134 8.26716L1.01816 8.26005L1.01717 8.25938L1.02099 8.25572L1.02438 8.2508C1.04224 8.22498 1.06258 8.20142 1.08524 8.18019Z" fill="#1A2E25" stroke="#1A2E25" strokeWidth="0.1"/></svg>
                  <span className="relative">{textData.Components.SideMenu.Logout[langState]}</span>
                </button>
              </div>
            </div>
          )}
          <ImpersonationBanner />
          <StickyHeaderProvider>
            <DashboardContent
              userRole={userRole}
              pathname={pathname}
              breadcrumbRoutes={breadcrumbRoutes}
              isVerified={isVerified}
              requiresPasswordReset={requiresPasswordReset}
              userEmail={userEmail}
            />
          </StickyHeaderProvider>
        </div>
      </div>
    </SidebarContext.Provider>
  )
}

function DashboardContent({
  userRole,
  pathname,
  breadcrumbRoutes,
  isVerified,
  requiresPasswordReset,
  userEmail,
}: {
  userRole: string;
  pathname: string;
  breadcrumbRoutes: { path: string; text: string }[];
  isVerified: boolean | null;
  requiresPasswordReset: boolean | null;
  userEmail: string | null;
}) {
  const { stickyHeaderActive } = useStickyHeader();

  return (
    <>
      {!stickyHeaderActive &&
        pathname !== "/dashboard" &&
        pathname !== "/dashboard/profile" && (
          <div className="sticky top-14 z-10 bg-background px-14 pt-3 pb-6">
            <Breadcrumb breadcrumbRoutes={breadcrumbRoutes} />
          </div>
        )}
      <Outlet />
      <ActionIndicator
        user={{
          isVerified,
          requiresPasswordReset,
          email: userEmail,
        }}
      />
    </>
  );
}

export default Dashboard;
